<template>
  <div v-if="shouldShowFocusPoint" class="flex flex-col items-start gap-2 self-stretch">
    <div class="flex w-full items-center justify-between">
      <UITextXsMedium>{{ t("common.style.focusPoint.title") }}</UITextXsMedium>
      <span class="cursor-pointer text-sm font-semibold text-[#004eeb]" @click="resetToCenter">
        {{ t("common.style.focusPoint.reset") }}
      </span>
    </div>
    <FocusPointPicker
      :image-url="currentImageUrl"
      :model-value="currentFocusPoint"
      @update:model-value="handleFocusPointChange"
    />
  </div>
</template>

<script setup lang="ts">
import { useAppStore } from "@/store/app";
import { IMAGE_LAYOUT, SURVEY_MODE } from "@/util/constants";
import { UITextXsMedium } from "@gohighlevel/ghl-ui";
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import FocusPointPicker from "./FocusPointPicker.vue";

const { t } = useI18n();
const store = useAppStore();

interface FocusPoint {
  x: number;
  y: number;
}

interface Props {
  isSurvey?: boolean;
  selectedSlideIndex?: number;
  isEditingSurveyDefault?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  isSurvey: false,
  selectedSlideIndex: 0,
  isEditingSurveyDefault: false,
});

const emit = defineEmits<{
  "update:focusPoint": [value: FocusPoint];
}>();

// Check if the current layout supports focus point (leftFixed, rightFixed) - only for desktop
const shouldShowFocusPoint = computed(() => {
  // Only show for desktop mode
  if (store.deviceMode === "mobile") {
    return false;
  }

  let layout: string;
  let showImage: boolean;
  let hasImage: boolean;

  if (props.isSurvey) {
    if (props.isEditingSurveyDefault) {
      const settings = store.surveyImageSettings.surveyDefault;
      layout = settings.desktopImageLayout || IMAGE_LAYOUT.TOP_FIXED;
      showImage = settings.showImage;
      hasImage =
        settings.headerImageSrc?.trim().length > 0 ||
        settings.mobileHeaderImageSrc?.trim().length > 0;
    } else {
      const slideId = store.slides[props.selectedSlideIndex]?.id;
      const slideSettings = store.surveyImageSettings.slideSettings?.[slideId];
      const slideMode = slideSettings?.mode || SURVEY_MODE.SURVEY_DEFAULT;

      if (slideMode === SURVEY_MODE.SURVEY_DEFAULT) {
        const settings = store.surveyImageSettings.surveyDefault;
        layout = settings.desktopImageLayout || IMAGE_LAYOUT.TOP_FIXED;
        showImage = settings.showImage;
        hasImage =
          settings.headerImageSrc?.trim().length > 0 ||
          settings.mobileHeaderImageSrc?.trim().length > 0;
      } else {
        const settings = slideSettings || {
          showImage: false,
          desktopImageLayout: IMAGE_LAYOUT.TOP_FIXED,
          mobileImageLayout: IMAGE_LAYOUT.HEADER,
          headerImageSrc: "",
          mobileHeaderImageSrc: "",
        };
        layout = settings.desktopImageLayout || IMAGE_LAYOUT.TOP_FIXED;
        showImage = settings.showImage;
        hasImage =
          settings.headerImageSrc?.trim().length > 0 ||
          settings.mobileHeaderImageSrc?.trim().length > 0;
      }
    }
  } else {
    // Form mode
    layout = store.builderConfig.desktopImageLayout || IMAGE_LAYOUT.TOP_FIXED;
    showImage = store.builderConfig.showImage || false;
    hasImage =
      store.formAction?.headerImageSrc?.trim().length > 0 ||
      store.formAction?.mobileHeaderImageSrc?.trim().length > 0;
  }

  // Only show focus point for leftFixed and rightFixed layouts
  return (
    showImage &&
    hasImage &&
    ([IMAGE_LAYOUT.LEFT_FIXED, IMAGE_LAYOUT.RIGHT_FIXED] as string[]).includes(layout)
  );
});

// Get the current image URL (desktop only)
const currentImageUrl = computed(() => {
  if (props.isSurvey) {
    if (props.isEditingSurveyDefault) {
      return store.surveyImageSettings.surveyDefault.headerImageSrc;
    }

    const slideId = store.slides[props.selectedSlideIndex]?.id;
    const slideSettings = store.surveyImageSettings.slideSettings?.[slideId];
    const slideMode = slideSettings?.mode || SURVEY_MODE.SURVEY_DEFAULT;

    if (slideMode === SURVEY_MODE.SURVEY_DEFAULT) {
      return store.surveyImageSettings.surveyDefault.headerImageSrc;
    }

    return slideSettings?.headerImageSrc || "";
  }

  // Form mode
  return store.formAction?.headerImageSrc || "";
});

// Get the current focus point (desktop only)
const currentFocusPoint = computed<FocusPoint>(() => {
  const defaultFocusPoint = { x: 50, y: 50 };

  if (props.isSurvey) {
    if (props.isEditingSurveyDefault) {
      return store.surveyImageSettings.surveyDefault.imageFocusPoint || defaultFocusPoint;
    }

    const slideId = store.slides[props.selectedSlideIndex]?.id;
    const slideSettings = store.surveyImageSettings.slideSettings?.[slideId];
    const slideMode = slideSettings?.mode || SURVEY_MODE.SURVEY_DEFAULT;

    if (slideMode === SURVEY_MODE.SURVEY_DEFAULT) {
      return store.surveyImageSettings.surveyDefault.imageFocusPoint || defaultFocusPoint;
    }

    return (slideSettings as any)?.imageFocusPoint || defaultFocusPoint;
  }

  // Form mode
  return (store.builderConfig as any).imageFocusPoint || defaultFocusPoint;
});

// Handle focus point change (desktop only)
const handleFocusPointChange = (focusPoint: FocusPoint) => {
  if (props.isSurvey) {
    if (props.isEditingSurveyDefault) {
      store.surveyImageSettings.surveyDefault.imageFocusPoint = focusPoint;
    } else {
      const slideId = store.slides[props.selectedSlideIndex]?.id;
      const slideSettings = store.surveyImageSettings.slideSettings?.[slideId];
      const slideMode = slideSettings?.mode || SURVEY_MODE.SURVEY_DEFAULT;

      if (slideMode === SURVEY_MODE.SURVEY_DEFAULT) {
        store.surveyImageSettings.surveyDefault.imageFocusPoint = focusPoint;
      } else {
        // Independent mode
        if (!store.surveyImageSettings.slideSettings[slideId]) {
          store.surveyImageSettings.slideSettings[slideId] = {
            mode: SURVEY_MODE.INDEPENDENT,
            showImage: false,
            desktopImageLayout: IMAGE_LAYOUT.TOP_FIXED,
            mobileImageLayout: IMAGE_LAYOUT.HEADER,
            headerImageSrc: "",
            mobileHeaderImageSrc: "",
          };
        }
        (store.surveyImageSettings.slideSettings[slideId] as any).imageFocusPoint = focusPoint;
      }
    }
  } else {
    // Form mode
    (store.builderConfig as any).imageFocusPoint = focusPoint;
  }
  emit("update:focusPoint", focusPoint);
  store.anyUnsavedChanges = true;
};

// Reset focus point to center (50, 50)
const resetToCenter = () => {
  handleFocusPointChange({ x: 50, y: 50 });
};
</script>
